<template>
  <div class="font-inter w- h-[1040px] bg-[#FFFFFF] rounded-t-[20px] mt-3">
    <Navbar />

    <div class="mx-8 my-6 space-y-6">
      <div class="hr-container space-x-5 mb-6">
        <div class="flex items-center justify-between">
          <div class="space-y-3">
            <p class="font-semibold text-bodyText text-[20px] space-x-3">
              Goals
            </p>
            <p class="text-bodyText text-[14px]">
              Set priorities, monitor progress, and stay on target.
            </p>
          </div>
          <button
            v-if="canCreateProject"
            @click="openCreateProjectModal"
            class="flex bg-black hover:bg-gray-400 text-primaryLight text-[14px] font-medium px-6 py-3 space-x-4 items-center rounded-[8px] h-[42px]"
            :disabled="goalLimit && goalLimit.is_limit_reached"
          >
            <img src="@/assets/dashboard/add.svg" alt="Add icon" class="h-4" />
            New Goal
          </button>
        </div>
      </div>

      <div>
        <div
          class="flex ml-auto justify-between mb-8 space-x-6 py-1 text-[14px] rounded-[6px] items-center"
        >
          <div class="flex gap-4 items-center">
            <p
              class="flex items-center cursor-pointer gap-2 rounded-[6px] bg-[#FFFFFF] bg-[14px] px-4 py-1 border border-grayLight"
            >
              <img src="@/assets/projects/sort.svg" alt="Icon" class="" />
              Filters
            </p>
            <div class="flex items-center gap-4">
              <!-- Status Filter -->
              <div ref="dropdown" @click="stopEventPropagation">
                <div
                  class="flex items-center gap-2 px-2 py-1 border border-grayLight rounded cursor-pointer"
                  @click="toggleStatusDropdown"
                >
                  {{ selectedStatus || "All" }}
                  <img src="@/assets/projects/chevron-down.svg" alt="Icon" />
                </div>
                <div
                  v-if="showStatusDropdown"
                  class="absolute bg-white border rounded-[6px] shadow mt-2"
                >
                  <ul>
                    <li
                      v-for="status in statuses"
                      :key="status"
                      @click="setStatusFilter(status)"
                      class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {{ status }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="flex space-x-3">
            <div
              @click="handleSearchClick"
              @mouseover="isSearchHovered = true"
              @mouseleave="isSearchHovered = false"
              :class="[
                'flex w-[36px] space-x-2 py-1 px-2 text-[14px] rounded-[6px] cursor-pointer transition-colors',
                'bg-[#F2F4F7]',
              ]"
            >
              <svg
                v-if="!isSearchHovered && viewMode !== 'search'"
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0834 18.1263C5.37508 18.1263 1.54175 14.293 1.54175 9.58463C1.54175 4.8763 5.37508 1.04297 10.0834 1.04297C14.7917 1.04297 18.6251 4.8763 18.6251 9.58463C18.6251 14.293 14.7917 18.1263 10.0834 18.1263ZM10.0834 2.29297C6.05841 2.29297 2.79175 5.56797 2.79175 9.58463C2.79175 13.6013 6.05841 16.8763 10.0834 16.8763C14.1084 16.8763 17.3751 13.6013 17.3751 9.58463C17.3751 5.56797 14.1084 2.29297 10.0834 2.29297Z"
                  fill="#292D32"
                />
                <path
                  d="M18.8338 18.9615C18.6754 18.9615 18.5171 18.9031 18.3921 18.7781L16.7254 17.1115C16.4838 16.8698 16.4838 16.4698 16.7254 16.2281C16.9671 15.9865 17.3671 15.9865 17.6088 16.2281L19.2754 17.8948C19.5171 18.1365 19.5171 18.5365 19.2754 18.7781C19.1504 18.9031 18.9921 18.9615 18.8338 18.9615Z"
                  fill="#292D32"
                />
              </svg>

              <svg
                v-else
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="#4C4D4F"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0834 18.1263C5.37508 18.1263 1.54175 14.293 1.54175 9.58463C1.54175 4.8763 5.37508 1.04297 10.0834 1.04297C14.7917 1.04297 18.6251 4.8763 18.6251 9.58463C18.6251 14.293 14.7917 18.1263 10.0834 18.1263ZM10.0834 2.29297C6.05841 2.29297 2.79175 5.56797 2.79175 9.58463C2.79175 13.6013 6.05841 16.8763 10.0834 16.8763C14.1084 16.8763 17.3751 13.6013 17.3751 9.58463C17.3751 5.56797 14.1084 2.29297 10.0834 2.29297Z"
                  fill="#292D32"
                />
                <path
                  d="M18.8338 18.9615C18.6754 18.9615 18.5171 18.9031 18.3921 18.7781L16.7254 17.1115C16.4838 16.8698 16.4838 16.4698 16.7254 16.2281C16.9671 15.9865 17.3671 15.9865 17.6088 16.2281L19.2754 17.8948C19.5171 18.1365 19.5171 18.5365 19.2754 18.7781C19.1504 18.9031 18.9921 18.9615 18.8338 18.9615Z"
                  fill="#222222"
                />
              </svg>
            </div>
            <div
              @click="viewMode = 'list'"
              @mouseover="isListHovered = true"
              @mouseleave="isListHovered = false"
              :class="[
                'flex w-[36px] space-x-2 py-1 px-2 text-[14px] rounded-[6px] cursor-pointer transition-colors',
                'bg-[#F2F4F7]',
              ]"
            >
              <svg
                v-if="!isListHovered && viewMode !== 'list'"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 6.00002H20M4 6.01002L4.01 5.99902M4 12.01L4.01 11.999M4 18.01L4.01 17.999M8 12H20M8 18H20"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <svg
                v-else
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="#4C4D4F"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="20" height="20" rx="3.33333" fill="#222222" />
                <path
                  d="M6.66659 4.99986H16.6666M3.33325 5.00819L3.34159 4.99902M3.33325 10.0082L3.34159 9.99902M3.33325 15.0082L3.34159 14.999M6.66659 9.99986H16.6666M6.66659 14.9999H16.6666"
                  stroke="white"
                  stroke-width="1.25"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div
              @click="viewMode = 'grid'"
              @mouseover="isGridHovered = true"
              @mouseleave="isGridHovered = false"
              :class="[
                'flex w-full space-x-2 py-1 px-2 text-[14px] rounded-[6px] cursor-pointer transition-colors',
                'bg-[#F2F4F7]',
              ]"
            >
              <svg
                v-if="!isGridHovered && viewMode !== 'grid'"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.5003 18.9587H7.50033C2.97533 18.9587 1.04199 17.0253 1.04199 12.5003V7.50033C1.04199 2.97533 2.97533 1.04199 7.50033 1.04199H12.5003C17.0253 1.04199 18.9587 2.97533 18.9587 7.50033V12.5003C18.9587 17.0253 17.0253 18.9587 12.5003 18.9587ZM7.50033 2.29199C3.65866 2.29199 2.29199 3.65866 2.29199 7.50033V12.5003C2.29199 16.342 3.65866 17.7087 7.50033 17.7087H12.5003C16.342 17.7087 17.7087 16.342 17.7087 12.5003V7.50033C17.7087 3.65866 16.342 2.29199 12.5003 2.29199H7.50033Z"
                  fill="#4C4D4F"
                />
                <path
                  d="M10 18.9587C9.65833 18.9587 9.375 18.6753 9.375 18.3337V1.66699C9.375 1.32533 9.65833 1.04199 10 1.04199C10.3417 1.04199 10.625 1.32533 10.625 1.66699V18.3337C10.625 18.6753 10.3417 18.9587 10 18.9587Z"
                  fill="#4C4D4F"
                />
                <path
                  d="M18.3337 10.625H1.66699C1.32533 10.625 1.04199 10.3417 1.04199 10C1.04199 9.65833 1.32533 9.375 1.66699 9.375H18.3337C18.6753 9.375 18.9587 9.65833 18.9587 10C18.9587 10.3417 18.6753 10.625 18.3337 10.625Z"
                  fill="#4C4D4F"
                />
              </svg>

              <svg
                v-else
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="#4C4D4F"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.25 12.75V22H7.81C4.17 22 2 19.83 2 16.19V12.75H11.25Z"
                />
                <path
                  d="M22 7.81V11.25H12.75V2H16.19C19.83 2 22 4.17 22 7.81Z"
                />
                <path d="M11.25 2V11.25H2V7.81C2 4.17 4.17 2 7.81 2H11.25Z" />
                <path
                  d="M22 12.75V16.19C22 19.83 19.83 22 16.19 22H12.75V12.75H22Z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          v-if="isInvestor"
          class="mx-auto w-[400px] border border-grayLight rounded-full align-center py-2 px-8 mb-8 flex items-center justify-between bg-white-to-gray"
        >
          <div class="flex space-x-2 items-center">
            <h1 class="text-[20px] text-bodyText font-bold">
              ${{ joinedProjectsCount }}
            </h1>
            <h1 class="text-[14px] text-grayDark">Total Amount Invested</h1>
          </div>
          <div class="h-8 w-[1px] bg-grayLight"></div>

          <div class="flex space-x-2 items-center">
            <h1 class="text-[20px] text-bodyText font-bold">
              {{ joinedProjectsCount }}
            </h1>
            <h1 class="text-[14px] text-grayDark">Joined Projects</h1>
          </div>
        </div>
        <div v-if="viewMode === 'list'">
          <!-- List View Component (Project List) -->
          <ProjectList
            :projects="filteredProjects"
            :selectedStatus="selectedStatus"
            :currentPage="currentPage"
            :totalPages="totalPages"
            @page-change="handlePageChange"
          />
        </div>
        <div v-else>
          <!-- Grid View Component (Kanban or Grid) -->
          <KanbanGrid
            :projects="filteredProjects"
            :investorProjects="investorProjects"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="isSideMenuOpen"
    class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40"
    @click="closeSideMenu"
  ></div>

  <div
    v-if="isSideMenuOpen"
    class="fixed top-0 right-0 w-[543px] h-full bg-white shadow-lg z-50 transform transition-transform duration-300 text-[14px]"
  >
    <div class="space-y-4">
      <!-- Header -->
      <div
        class="py-4 px-4 flex justify-between items-center border-b-[1px] border-grayLight"
      >
        <h3 class="text-lg font-medium">Search for projects</h3>
        <div
          @click="closeSideMenu"
          class="bg-[#FAFBFC] h-8 w-8 rounded-full flex items-center justify-center"
        >
          <img
            src="@/assets/tasks/iconoir_cancel.svg"
            alt="Close"
            class="h-6 cursor-pointer"
          />
        </div>
      </div>

      <ProjectSearch
        ref="projectSearch"
        :projects="allProjects"
        :loading="loading"
        :isManager="isManager"
        :isAdmin="isAdmin"
        :isTalent="isTalent"
        :isInvestor="isInvestor"
        @close="closeSideMenu"
        @view-project="goToProject"
        @create-project="createProjectFromSearch"
      />
    </div>
  </div>
  <CreateProjectModal
    ref="createProjectModal"
    :showModal="showCreateProjectModal"
    @close="closeCreateProjectModal"
    @projectCreated="handleProjectCreated"
    @refresh="fetchProjects"
  />

  <!-- <InvestorProjects
    v-if="isInvestor"
    :projects="joinedProjects"
    :joined-projects="joinedProjects"
    :loading="loading"
    :total-amount-invested="totalAmountInvested"
  /> -->
  <div v-if="showGoalLimitToast" class="fixed top-6 right-6 z-50">
    <div
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <span class="block sm:inline">{{ goalLimitToastMessage }}</span>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import Navbar from "../NavbarComponent.vue";
import KanbanGrid from "./KanbanGrid.vue";
import ProjectList from "./ProjectList.vue";
import CreateProjectModal from "../modals/CreateProjectModal.vue";
import ProjectSearch from "../search/ProjectSearch.vue";
// import InvestorProjects from "./InvestorProjects.vue";

export default {
  name: "ProjectsComponent",
  components: {
    Navbar,
    KanbanGrid,
    ProjectList,
    CreateProjectModal,
    ProjectSearch,
    // InvestorProjects,
  },

  data() {
    return {
      allProjects: [], // Store all projects for search functionality

      isListHovered: false,
      isGridHovered: false,
      isSearchHovered: false,
      isSideMenuOpen: false,
      viewMode: "list",
      projects: [],
      investorProjects: [],
      joinedProjects: [],
      totalAmountInvested: 0,
      filteredProjects: [],
      loading: true,
      managedProjects: [],
      selectedStatus: "All", // Initial status for showing all projects
      showCreateProjectModal: false,
      showStatusDropdown: false,
      statuses: ["All", "Active", "Completed", "On Hold"],
      goalLimit: null,
      showGoalLimitToast: false,
      goalLimitToastMessage: "",
      currentPage: 1,
      totalPages: 1,
      canCreateProject: false
    };
  },
  watch: {
    viewMode(newValue) {
      if (newValue === "search") {
        this.openSideMenu();
      }
    },
    currentPage(newPage) {
      if (newPage > 0) {
        this.fetchProjects(newPage);
      }
    },
  },

  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
    limitedProjects() {
      return this.filteredProjects.slice(0, 6); // Display only the first 6 projects
    },
    joinedProjectsCount() {
      // Handle both array and undefined/null cases
      return Array.isArray(this.joinedProjects)
        ? this.joinedProjects.length
        : 0;
    },
  },
  methods: {
    stopEventPropagation(event) {
      if (event) {
        event.stopPropagation();
      }
    },
    toggleStatusDropdown(event) {
      if (event) {
        event.stopPropagation();
      }
      this.showStatusDropdown = !this.showStatusDropdown;
    },
    closeStatusDropdown() {
      this.showStatusDropdown = false;
    },

    setStatusFilter(status) {
      this.selectedStatus = status;
      this.showStatusDropdown = false;
      console.log("Selected project status updated to:", this.selectedStatus);

      this.saveStatusToStorage(status);

      this.filterProjectsByStatus();
    },

    applyFilters() {
      console.log("Applying Filters:", this.filters);
    },
    openSideMenu(member) {
      this.selectedMember = member;
      this.isSideMenuOpen = true;
    },
    closeSideMenu() {
      this.isSideMenuOpen = false;
      this.selectedReport = null;
    },

    openCreateProjectModal() {
      this.showCreateProjectModal = true;
      if (this.$refs.createProjectModal) {
        this.$refs.createProjectModal.clearErrorMessage(); // Use the ref to call the method
      }
    },

    closeCreateProjectModal() {
      this.showCreateProjectModal = false;
    },
    handleProjectCreated(message) {
      this.$toast.success(message);
      this.fetchProjects(this.currentPage); // Refresh projects after creation
    },
    handlePageChange(page) {
      this.currentPage = page;
    },

    setViewMode(mode) {
      this.viewMode = mode;
    },
    async fetchCurrentUser() {
      try {
        const response = await apiClient.post("/current_user");
        this.user = response.data.user;
        this.fetchGoalLimit(response.data.user.user_id);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },
    async fetchProjects(page = 1) {
      this.loading = true;
      try {
        const response = await apiClient.get("/web/projects_all", {
          params: {
            page: page,
            page_size: 100,
            active_profile: this.isAdmin ? "admin" : this.isManager ? "manager" : this.isInvestor ? "investor" : this.isTalent ? "talent" : "",
          },
        });
        this.projects = response.data.data || [];
        this.allProjects = response.data.data || [];
        this.totalPages = response.data.meta.total_pages || 1; // Set total pages from response
        // Initialize the filtered projects with all projects
        this.filteredProjects = response.data.data || [];
        this.$nextTick(() => {
          this.filterProjectsByStatus();
        });
        console.log("After Fetched projects:", this.filteredProjects);

        this.emitInvestorData();
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        this.loading = false;
        this.emitInvestorData();
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = date.getMonth() + 1; // Months are zero-based
      const day = date.getDate();
      return `${month}/${day}`;
    },

    filterProjectsByStatus() {
      // Update the filteredProjects
      if (this.selectedStatus === "All") {
        this.filteredProjects = [...this.allProjects];
      } else {
        this.filteredProjects = this.allProjects.filter(
          (project) =>
            (project.status || project.project_status) === this.selectedStatus
        );
      }

      // Emit status change to child components
      this.$emit("status-changed", this.selectedStatus);
    },
    saveStatusToStorage(status) {
      const currentUser = this.$store.state.user;
      const userId = currentUser ? currentUser.user_id : "guest";

      try {
        localStorage.setItem(
          `selectedProjectStatus_${userId}`,
          JSON.stringify(status)
        );
        console.log("Status saved to localStorage:", status);
      } catch (error) {
        console.error("Error saving status to localStorage:", error);
      }
    },
    loadSavedStatus() {
      const currentUser = this.$store.state.user;
      const userId = currentUser ? currentUser.user_id : "guest";

      try {
        const savedStatus = localStorage.getItem(
          `selectedProjectStatus_${userId}`
        );
        if (savedStatus) {
          const parsedStatus = JSON.parse(savedStatus);
          this.selectedStatus = parsedStatus;
          console.log("Loaded saved status:", parsedStatus);

          // this.filterProjectsByStatus();
        }
      } catch (error) {
        console.error("Error loading status from localStorage:", error);
        this.selectedStatus = "All"; // Fallback
      }
    },
    handleStatusChange(status) {
      this.selectedStatus = status;
    },
    openSearchSidebar() {
      // First fetch all projects to ensure we have the latest data
      this.refreshProjects().then(() => {
        this.isSideMenuOpen = true;
        this.viewMode = "search";

        // Reset search in case it was populated from previous search
        if (this.$refs.projectSearch) {
          this.$refs.projectSearch.resetSearch();
        }
      });
    },

    async refreshProjects() {
      this.loading = true;
      try {
        await this.fetchProjects(this.currentPage);

        // Store all projects in a separate array for search
        this.allProjects = [...this.projects];
      } catch (error) {
        console.error("Error refreshing projects:", error);
      } finally {
        this.loading = false;
      }
    },

    goToProject(projectId) {
      this.closeSideMenu();
      this.$router.push({ name: "ProjectDetails", params: { projectId } });
    },

    createProjectFromSearch() {
      this.closeSideMenu();
      this.openCreateProjectModal();
    },

    handleSearchClick() {
      this.viewMode = "search";
      this.openSearchSidebar();
    },

    emitInvestorData() {
      if (this.isInvestor) {
        this.$emit("investor-data-updated", {
          joinedProjects: this.joinedProjects,
          totalAmountInvested: this.totalAmountInvested,
          loading: this.loading,
        });
      }
    },
    async fetchGoalLimit(userId) {
      try {
        const response = await apiClient.get("/web/limit/", {
          params: {
            id: userId,
            name: "project",
            limit_type: "user",
          },
        });
        this.goalLimit = response.data.data;
        // Handle toast and button state
        if (this.goalLimit.is_limit_reached) {
          this.goalLimitToastMessage =
            "You have reached the maximum number of goals you can create.";
          this.showGoalLimitToast = true;
        } else if (this.goalLimit.display_warning_banner) {
          const remaining =
            this.goalLimit.max_count - this.goalLimit.current_count;
          this.goalLimitToastMessage = `Warning: Only ${remaining} goals left to create.`;
          this.showGoalLimitToast = true;
        } else {
          this.showGoalLimitToast = false;
          this.goalLimitToastMessage = "";
        }
      } catch (error) {
        console.error("Error fetching goal limit:", error);
      }
    },
    async checkCanCreateProject() {
      try {
        const response = await apiClient.get("/web/projects/can-create");
        this.canCreateProject = response.data.data;
      } catch (error) {
        console.error("Error checking project creation permission:", error);
        this.canCreateProject = false;
      }
    },
  },
  async created() {
    this.loadSavedStatus();
    await this.checkCanCreateProject(); // Check permission first
    await this.fetchProjects(this.currentPage); // Then fetch projects
  },
  mounted() {
    document.addEventListener("click", this.closeStatusDropdown);
  },
  beforeUnmount() {
    // Clean up the event listener
    document.removeEventListener("click", this.closeStatusDropdown);
  },
};
</script>
